import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import KPICard from '../components/KPICard'

function WorkoverOverview() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [lastFetched, setLastFetched] = useState(null)

    useEffect(() => { fetchData() }, [])

    // Auto-refresh every 10 minutes
    useEffect(() => {
        const interval = setInterval(() => fetchData(), 10 * 60 * 1000)
        return () => clearInterval(interval)
    }, [])

    const fetchData = async () => {
        setLoading(true)
        setError(null)
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const res = await fetch(`${apiConfig.baseUrl}/workovers`)
                if (!res.ok) throw new Error('Failed to fetch workover data')
                const json = await res.json()
                setData(json)
                setLastFetched(new Date())
                setLoading(false)
                return
            } catch (err) {
                if (attempt === 1) {
                    setError(err.message)
                    setLoading(false)
                    return
                }
                await new Promise(r => setTimeout(r, 3000))
            }
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (error && !data) {
        return (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                <p className="font-semibold">Error loading workover data</p>
                <p className="text-sm mt-1">{error}</p>
                <button onClick={fetchData}
                    className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                    Retry
                </button>
            </div>
        )
    }

    const kpis = data?.kpis || {}
    const active = data?.active || []
    const allWellsDown = data?.wellsDown || []
    const wellsDown = allWellsDown.filter(w => w.category === 'down')
    const wellsInactive = allWellsDown.filter(w => w.category === 'inactive')
    const rigs = data?.rigs || []
    const rigQueue = data?.rigQueue || []

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div>
                    <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                        <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                        Workovers
                    </h1>
                    <p className={`${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                        Last updated: {lastFetched ? lastFetched.toLocaleString() : 'Loading...'}
                        <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-gray-400'}`}>
                            (auto-refreshes every 10 min)
                        </span>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => navigate('/workovers/analytics')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}>
                        Analytics
                    </button>
                    <button onClick={() => navigate('/workovers/history')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}>
                        History
                    </button>
                    <button onClick={() => navigate('/workovers/schedule')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}>
                        Schedule
                    </button>
                    <button onClick={fetchData}
                        className={`px-2.5 py-1.5 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`} title="Refresh data">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <KPICard
                    title="Active Workovers"
                    value={kpis.activeWorkovers}
                    unit=""
                    subtitle={`${kpis.pendingWorkovers || 0} pending`}
                />
                <KPICard
                    title="MTD Spend"
                    value={`$${(kpis.spendThisMonth || 0).toLocaleString()}`}
                    unit=""
                    subtitle={`${kpis.completedThisMonth || 0} completed`}
                    delta={kpis.spendDelta}
                />
                <KPICard
                    title="Wells Down"
                    value={kpis.wellsDown}
                    unit=""
                    subtitle={`${kpis.wellsInactive || 0} inactive/shut-in`}
                />
                <KPICard
                    title="Deferred Revenue"
                    value={`$${(kpis.deferredRevenue || 0).toLocaleString()}`}
                    unit=""
                    subtitle={`WTI: $${kpis.wtiPrice?.toFixed(2) || '--'}/BBL`}
                />
            </div>

            {/* Active Workovers Table */}
            <div className="chart-container">
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                    Active Workovers
                </h2>
                {active.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>WO #</th>
                                    <th>Well</th>
                                    <th>Rig</th>
                                    <th>Type</th>
                                    <th>Reason</th>
                                    <th className="text-right">Est. Cost</th>
                                    <th className="text-right">Def. BOPD</th>
                                    <th className="text-right">Days</th>
                                </tr>
                            </thead>
                            <tbody>
                                {active.map((w) => (
                                    <tr key={w.id} className="cursor-pointer"
                                        onClick={() => navigate(`/workovers/detail/${w.id}`)}>
                                        <td className="font-mono text-sm">{w.woNumber || (w.source === 'snowflake' ? w.workType || 'PV Job' : `WO-${w.id}`)}</td>
                                        <td className="font-medium">
                                            {w.well}
                                            <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>&rarr;</span>
                                        </td>
                                        <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{w.rig || '--'}</td>
                                        <td>{w.workType || '--'}</td>
                                        <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{w.reason || '--'}</td>
                                        <td className="text-right font-mono">
                                            {w.estCost ? `$${w.estCost.toLocaleString()}` : '--'}
                                        </td>
                                        <td className="text-right font-mono">{w.defBopd?.toFixed(1) || '--'}</td>
                                        <td className="text-right">
                                            <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                                                w.daysOngoing > 14
                                                    ? theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                                                    : w.daysOngoing > 7
                                                    ? theme === 'dark' ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                                                    : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                            }`}>
                                                {w.daysOngoing}d
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        No active workovers
                    </p>
                )}
            </div>

            {/* Wells Down Table - only active failures */}
            <div className="chart-container">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Wells Down ({wellsDown.length})
                        </h2>
                        {wellsInactive.length > 0 && (
                            <span className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-slate-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                +{wellsInactive.length} inactive/shut-in
                            </span>
                        )}
                    </div>
                    <button onClick={() => navigate('/workovers/wells-down')}
                        className={`text-sm ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                        View All &rarr;
                    </button>
                </div>
                {wellsDown.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Well</th>
                                    <th>Failure Type</th>
                                    <th>Detail</th>
                                    <th className="text-right">Avg BOPD</th>
                                    <th className="text-right">Days Down</th>
                                    <th className="text-right">Deferred (BBL)</th>
                                    <th className="text-right">Revenue Impact</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wellsDown.slice(0, 10).map((w, i) => (
                                    <tr key={w.downtimeId || i}>
                                        <td className="font-medium">{w.wellName}</td>
                                        <td>{w.downtimeCode || '--'}</td>
                                        <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                            {w.downtimeCode2 || '--'}
                                        </td>
                                        <td className="text-right font-mono">{w.avgBopd}</td>
                                        <td className="text-right">
                                            <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                                                w.daysDown > 14
                                                    ? theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                                                    : w.daysDown > 7
                                                    ? theme === 'dark' ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                                                    : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                            }`}>
                                                {w.daysDown}d
                                            </span>
                                        </td>
                                        <td className="text-right font-mono">{w.deferredOil?.toLocaleString()}</td>
                                        <td className="text-right font-mono text-red-400">
                                            ${w.revenueImpact?.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className={`text-sm text-green-400`}>All wells operational</p>
                )}
            </div>

            {/* Rigs + Rig Queue side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Rigs */}
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Rigs
                    </h2>
                    {rigs.length > 0 ? (
                        <div className="space-y-3">
                            {rigs.map(r => (
                                <div key={r.id} className={`flex justify-between items-center p-3 rounded-lg ${
                                    theme === 'dark' ? 'bg-slate-800' : 'bg-gray-50'
                                }`}>
                                    <div>
                                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{r.name}</p>
                                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {r.contractor} &middot; ${r.dayRate?.toLocaleString()}/day
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                                            r.status === 'Active'
                                                ? theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                                : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {r.status}
                                        </span>
                                        {r.currentWell && (
                                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {r.currentWell}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No rigs configured</p>
                    )}
                </div>

                {/* Rig Queue */}
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Rig Queue ({rigQueue.length}/5)
                    </h2>
                    {rigQueue.length > 0 ? (
                        <div className="space-y-3">
                            {rigQueue.map((q, i) => (
                                <div key={q.id} className={`flex items-start gap-3 p-3 rounded-lg ${
                                    theme === 'dark' ? 'bg-slate-800' : 'bg-gray-50'
                                }`}>
                                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                        theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {q.position}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{q.well}</p>
                                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {q.workType || '--'} &middot; {q.reason || '--'}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-sm font-mono ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {q.costEstimate ? `$${q.costEstimate.toLocaleString()}` : '--'}
                                        </p>
                                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {q.bopdImpact ? `${q.bopdImpact} BOPD` : ''} {q.daysDown ? `${q.daysDown}d down` : ''}
                                        </p>
                                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium mt-1 ${
                                            q.fundingType === 'Capital'
                                                ? theme === 'dark' ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                                                : theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {q.fundingType || 'Operating'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Queue empty</p>
                    )}
                </div>
            </div>

            {/* YTD Summary Strip */}
            <div className={`flex items-center justify-between px-4 py-3 rounded-lg ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-gray-200'}`}>
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    YTD Workovers
                </span>
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {kpis.ytdCount || 0} completed &middot; ${(kpis.ytdSpend || 0).toLocaleString()} total spend
                </span>
            </div>
        </div>
    )
}

export default WorkoverOverview
